import {useState} from "react";
import {clsx} from "clsx";
import type {FieldError} from "@/shared/timr";
import {CutHandlingMode} from "@/shared/timrTypes";
import {Collapsible} from "@/primitives/Collapsible";
import {Select} from "@/primitives/Select";
import {Switch} from "@/primitives/Switch";
import {FA6Icon} from "@/primitives/FA6Icon";
import {NumberInput} from "@/shared/ui/primitives/NumberInput";
import {millisToMinutes, minutesToMillis} from "@/shared/utils/misc";
import {type CutHandlingConfiguration, toCutHandlingConfiguration} from "@/features/admin/breakModel/cutHandlingConfigurationModel";
import styles from "./CutHandlingConfigurationPanel.module.css";

export type {CutHandlingConfiguration};

interface CutHandlingConfigurationPanelProps {
	/** Current cut-handling configuration stored on the dialog's break model; its threshold only seeds step 2. */
	configuration: CutHandlingConfiguration;
	/** Server-side validation errors that belong to cut-handling fields. */
	fieldErrors: FieldError[];
	/** Emits a normalized configuration whenever the user changes the mode or the warning threshold. */
	onChange: (configuration: CutHandlingConfiguration) => void;
}

const CUT_HANDLING_MODE_ITEMS = [
	{value: CutHandlingMode.DOCUMENT, label: messages["breakModel.cutHandlingMode.DOCUMENT"]},
	{value: CutHandlingMode.VALIDATION, label: messages["breakModel.cutHandlingMode.VALIDATION"]}
];

/**
 * Cut-handling editor for the break-model dialog's Cuts tab. Step 2 only applies while the mode is
 * DOCUMENT - VALIDATION already warns on every capped minute, so step 2 slides out then.
 */
export function CutHandlingConfigurationPanel({configuration, fieldErrors, onChange}: Readonly<CutHandlingConfigurationPanelProps>) {
	// Kept here, as switching step 2 off or the mode to VALIDATION propagates null.
	const [thresholdEnabled, setThresholdEnabled] = useState(configuration.cutWarningThresholdDuration !== null);
	const [cutWarningThresholdDuration, setCutWarningThresholdDuration] = useState(configuration.cutWarningThresholdDuration ?? 0);

	const thresholdApplicable = configuration.cutHandlingMode === CutHandlingMode.DOCUMENT;
	const thresholdError = fieldErrors.find(fieldError => fieldError.field === "cutWarningThresholdDuration");

	const handleCutHandlingModeChange = (cutHandlingMode: CutHandlingMode) => {
		onChange(toCutHandlingConfiguration(cutHandlingMode, thresholdEnabled, cutWarningThresholdDuration));
	};

	const handleThresholdEnabledChange = (enabled: boolean) => {
		setThresholdEnabled(enabled);
		onChange(toCutHandlingConfiguration(configuration.cutHandlingMode, enabled, cutWarningThresholdDuration));
	};

	const handleThresholdMinutesChange = (minutes: number) => {
		const duration = minutesToMillis(minutes);
		setCutWarningThresholdDuration(duration);
		onChange(toCutHandlingConfiguration(configuration.cutHandlingMode, thresholdEnabled, duration));
	};

	return (
		<>
			<div className={clsx(styles.step, styles.stepPrimary, !thresholdApplicable && styles.warning)}>
				<span className={clsx("badge", !thresholdApplicable ? "bg-orange" : "badge-primary")}>1</span>
				<div className={styles.text}>
					<span className="display-block text-semibold">{messages["breakModel.cutHandlingMode.title"]}</span>
					<span className="display-block text-muted text-size-mini">{messages["breakModel.cutHandlingMode.subtitle"]}</span>
				</div>
				<div className={styles.control}>
					<Select.Root items={CUT_HANDLING_MODE_ITEMS} value={configuration.cutHandlingMode} onValueChange={handleCutHandlingModeChange}>
						<Select.Trigger id="cutHandlingModeSelect"/>
						<Select.Items/>
					</Select.Root>
				</div>
			</div>

			<Collapsible open={thresholdApplicable}>
				<div className={clsx(styles.step, styles.thresholdStep, thresholdEnabled && styles.warning)}>
					<span className={clsx("badge", thresholdEnabled ? "bg-orange" : "badge-default")}>2</span>
					<label className={clsx(styles.text, "checkbox-inline flex-vertical-center p-0")}>
						<Switch checked={thresholdEnabled} onCheckedChange={handleThresholdEnabledChange}/>
						<span className="position-right">{messages["breakModel.cutWarningThreshold.label1"]}</span>
					</label>
					<div className={clsx(styles.control, thresholdError && "has-error")}>
						<div className="flex-vertical-center">
							<NumberInput
								id="cutWarningThresholdDurationField"
								className={styles.thresholdInput}
								value={millisToMinutes(cutWarningThresholdDuration)}
								onValueChange={handleThresholdMinutesChange}
								disabled={!thresholdEnabled}
								maxLength={5}
								ariaLabel={messages["breakModel.cutWarningThreshold.label1"]}
							/>
							<span className="position-right">{messages["breakModel.cutWarningThreshold.label2"]}</span>
						</div>
						<span className={clsx("text-nowrap", thresholdEnabled && "text-orange")}>
							<FA6Icon icon="triangle-exclamation" className="position-left"/>
							{messages["breakModel.cutHandlingMode.VALIDATION"]}
						</span>
					</div>
				</div>
				{thresholdError && (
					<div className="has-error">
						<span className="help-block">{thresholdError.message}</span>
					</div>
				)}
			</Collapsible>
		</>
	);
}
